/**
 * Zentrale Datum-/Uhrzeit-Formatierung — immer deutsches Format, 24h.
 *
 * date-fns statt `toLocaleDateString("de-DE")`: letzteres fällt ohne
 * ICU-Daten still auf en-US zurück ("09/18/2026", "2:32 PM"). date-fns
 * formatiert unabhängig von Runtime-/Browser-Locale.
 */
import { format, parseISO } from "date-fns";
import { de, enUS } from "date-fns/locale";

function toDate(value) {
  if (!value && value !== 0) return null;
  try {
    if (typeof value === "string") {
      return value.length === 10 ? parseISO(`${value}T00:00:00`) : parseISO(value);
    }
    return new Date(value);
  } catch {
    return null;
  }
}

function safeFormat(value, pattern, fallback = "") {
  const d = toDate(value);
  if (!d || isNaN(d.getTime())) return fallback;
  try {
    return format(d, pattern, { locale: de });
  } catch {
    return fallback;
  }
}

// --- Datum -----------------------------------------------------------------
// 18.09.2026
export const deDate = (v) => safeFormat(v, "dd.MM.yyyy");
export const DE_NUMERIC = (v) => safeFormat(v, "dd.MM.yyyy", v || "");

// 18. Sep.
export const deDateShort = (v) => safeFormat(v, "dd. MMM");

// 18. Sep. 2026
export const DE_DATE = (v) => safeFormat(v, "dd. MMM yyyy", v || "");
export const deDateMedium = (v) => safeFormat(v, "dd. MMM yyyy");

// 18. Sep. 26
export const deDateMediumShortYear = (v) => safeFormat(v, "dd. MMM yy");

// 18. September
export const deDayMonth = (v) => safeFormat(v, "dd. MMMM");

// 18. September 2026
export const deDateFull = (v) => safeFormat(v, "dd. MMMM yyyy");

// Freitag, 18. September 2026
export const deDateLong = (v) => safeFormat(v, "EEEE, d. MMMM yyyy");
export const DE_LONG = (v) => safeFormat(v, "EEEE, d. MMMM yyyy", v || "");
// EN long date — „Thursday, September 24, 2026" (US-Locale, unabhängig vom Browser).
export const enDateLong = (v) => {
  const d = toDate(v);
  if (!d || isNaN(d.getTime())) return "";
  try { return format(d, "EEEE, MMMM d, yyyy", { locale: enUS }); } catch { return ""; }
};

// Fr., 18. September
export const deWeekdayDayMonth = (v) => safeFormat(v, "EEEEEE., dd. MMMM");

// Fr., 18. Sep.
export const deWeekdayDateShort = (v) => safeFormat(v, "EEEEEE., dd. MMM");

// September 2026
export const deMonthYear = (v) => safeFormat(v, "MMMM yyyy");

// --- Uhrzeit (immer 24h) ---------------------------------------------------
// 14:32
export const deTime = (v) => safeFormat(v, "HH:mm");

// --- Datum + Uhrzeit -------------------------------------------------------
// 18.09.2026, 14:32
export const deDateTime = (v) => safeFormat(v, "dd.MM.yyyy, HH:mm");
// 24 Jun 2026 · Gelesen-Hinweis: „TT.MM. um HH:MM" (Read-Receipt-Stempel).
export const deSeenStamp = (v) => safeFormat(v, "dd.MM. 'um' HH:mm");

// 18. Sep., 14:32
export const deDateShortTime = (v) => safeFormat(v, "dd. MMM, HH:mm");

// 18. Sep. 2026, 14:32
export const DE_DATETIME = (v) => safeFormat(v, "dd. MMM yyyy, HH:mm", v || "");
export const deDateMediumTime = (v) => safeFormat(v, "dd. MMM yyyy, HH:mm");

// 18. September, 14:32
export const deDayMonthTime = (v) => safeFormat(v, "dd. MMMM, HH:mm");

// 18. September 2026, 14:32
export const deDateFullTime = (v) => safeFormat(v, "dd. MMMM yyyy, HH:mm");

// Fr., 18. Sep., 14:32
export const deWeekdayDateShortTime = (v) => safeFormat(v, "EEEEEE., dd. MMM, HH:mm");

// Fr., 18. September 2026, 14:32
export const deWeekdayDateFullTime = (v) => safeFormat(v, "EEEEEE., dd. MMMM yyyy, HH:mm");
